import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type HandoverRequest, canInAnyScope, chainOrderEnforced } from '@testflow/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IdLabel } from '../../components/atoms.js';
import { CoverageMilestone } from '../../components/gamification/coverage-milestone.js';
import { StreakChip } from '../../components/gamification/streak-chip.js';
import { TeamGoalBar } from '../../components/gamification/team-goal-bar.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { ResizablePanel } from '../../components/resizable-panel.js';
import { ClipTooltip } from '../../components/tooltip.js';
import { type TreeProcess, catalogApi } from '../../lib/catalog-api.js';
import { mapApiError } from '../../lib/errors.js';
import { gamificationApi } from '../../lib/gamification-api.js';
import { handoverApi } from '../../lib/handover-api.js';
import { selectedEntryCount } from '../../lib/handover-selection.js';
import { pickLang } from '../../lib/pick-lang.js';
import { defaultActivePlanId, sortActivePlans } from '../../lib/plan-selection.js';
import { plansApi } from '../../lib/plans-api.js';
import {
  type PlaylistEntryRef,
  isBlocked,
  nextAfterSubmit,
  playlistEntryKey,
} from '../../lib/playlist-groups.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { runsApi } from '../../lib/runs-api.js';
import { toast } from '../../lib/toast.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { useRowSelection } from '../../lib/use-row-selection.js';
import { useUrlState } from '../../lib/use-url-state.js';
import { ChainGroups } from './chain-groups.js';
import { ColleaguesPanel, countColleagueEntries } from './colleagues-panel.js';
import { ExecutionPanel } from './execution-panel.js';
import { fixCheckMatches } from './fix-check-card.js';
import { FixCheckDetail } from './fix-check-detail.js';
import { MyDefectPanel } from './my-defect-panel.js';
import { MyDefects } from './my-defects.js';
import { Playlist, type PlaylistHandover } from './playlist.js';
import { TestCasePanel } from './test-case-panel.js';
import { nextFixCheck, useFixChecks } from './use-fix-checks.js';

// Design-Spec §3.3 (Screen 2b dark / 1c light): Tester-Startbildschirm. Umschalter Playlist ⇄
// Baum-Ansicht (EXE-3), seit #128 mit der Playlist in zwei Sichten (Status / E2E-Prozess).
// Auswahl einer Karte setzt `selected` (Task 3: das PAAR aus Testfall und Kette, siehe
// lib/playlist-groups.ts); das
// Ausführungspanel (Task 21) rendert im rechten Slot. Nach jedem Submit ruft es `onSubmitted` —
// hier wird daraus der Auto-Sprung (EXE-1): der nächste Eintrag nach dem aktuellen in der (noch
// nicht neu geladenen) Playlist wird ausgewählt, sonst der erste verbleibende, sonst wird die
// Auswahl geleert.
//
// #55: mehrere Testpläne dürfen gleichzeitig aktiv sein — vorher griff `plans.find(is_active)`,
// bei zwei aktiven Plänen also ein zufälliger. Ersetzt durch einen sichtbaren Umschalter (nur ab
// zwei aktiven Plänen, sonst bleibt die Ansicht so schlank wie vorher), dessen Wahl PRO PROJEKT
// in `localStorage` gemerkt wird. Details am State unten.

// #128: dritte Ansicht 'e2e' — „Meine Tests" nach E2E-Prozess gruppiert (chain-groups.tsx). Der
// URL-Wert 'playlist' bleibt der Name der Status-Ansicht, damit bestehende Links weiter gelten.
type ViewMode = 'playlist' | 'e2e' | 'tree';

// #194 (Nachtrag 14.10.): Gewählt ist ENTWEDER ein Playlist-Eintrag (rechts die Ausführung) ODER
// eine Karte „Fehler prüfen" (rechts das Defect-Detail mit „Behoben"/„Nicht behoben"). Ein Zustand
// statt zweier, damit nie beides zugleich gewählt sein kann.
type MyTestsSelection =
  | { kind: 'entry'; entry: PlaylistEntryRef }
  | { kind: 'fix_check'; defect_id: string };

function TreeTestCase({
  testCaseId,
  humanId,
  title,
  selected,
  onSelect,
}: {
  testCaseId: string;
  humanId: string;
  title: string;
  selected: boolean;
  onSelect: (testCaseId: string) => void;
}) {
  return (
    // #95: ClipTooltip umschließt den fokussierbaren <button> (nicht umgekehrt) — nur so bubbelt
    // dessen Fokus-Event in den ClipTooltip-Wrapper und löst die Blase auch per Tastatur aus
    // (Muster aus tooltip.test.tsx). line-clamp-2 statt truncate macht den Normalfall ohne
    // Interaktion lesbar (#95, Entscheidung „Umbruch UND Breite").
    <ClipTooltip text={title} className="block w-full">
      <button
        type="button"
        onClick={() => onSelect(testCaseId)}
        className={`flex w-full items-center gap-2 rounded-btn px-2 py-1 text-left text-sm ${
          selected
            ? 'bg-brand-accent/10 ring-1 ring-inset ring-brand-accent'
            : 'hover:bg-[var(--bg-subtle)]'
        }`}
      >
        <IdLabel id={humanId} />
        <span data-clip className="flex-1 line-clamp-2 text-text-body">
          {title}
        </span>
      </button>
    </ClipTooltip>
  );
}

function TreeProcessNode({
  process,
  projectId,
  selectedTestCaseId,
  onSelect,
}: {
  process: TreeProcess;
  projectId: string;
  selectedTestCaseId: string | null;
  onSelect: (testCaseId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const [expanded, setExpanded] = useState(false);
  const processName = pickLang(lang, process.name_de, process.name_en);

  const testCasesQuery = useQuery({
    queryKey: ['tcs', projectId, process.process_id],
    queryFn: () => catalogApi.listTestCases(projectId, process.process_id),
    enabled: expanded,
  });

  return (
    <div>
      <ClipTooltip text={processName} className="block w-full">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex w-full items-center gap-2 px-3 py-1.5 pl-6 text-left text-sm text-text-body hover:bg-[var(--bg-subtle)]"
        >
          <span className="text-text-muted">{expanded ? '▾' : '▸'}</span>
          <span data-clip className="flex-1 line-clamp-2">
            {processName}
          </span>
          <span className="text-[11px] text-text-muted">{process.test_case_count}</span>
        </button>
      </ClipTooltip>
      {expanded && (
        <div className="pl-9">
          {testCasesQuery.isLoading && (
            <div className="px-2 py-1 text-xs text-text-muted">{t('catalog.loading')}</div>
          )}
          {testCasesQuery.data?.map((tc) => (
            <TreeTestCase
              key={tc.test_case_id}
              testCaseId={tc.test_case_id}
              humanId={tc.human_id}
              title={pickLang(lang, tc.title_de, tc.title_en)}
              selected={tc.test_case_id === selectedTestCaseId}
              onSelect={onSelect}
            />
          ))}
          {testCasesQuery.data?.length === 0 && (
            <div className="px-2 py-1 text-xs text-text-muted">{t('catalog.empty')}</div>
          )}
        </div>
      )}
    </div>
  );
}

function TreeGroup({
  label,
  processes,
  projectId,
  selectedTestCaseId,
  onSelect,
}: {
  label: string;
  processes: TreeProcess[];
  projectId: string;
  selectedTestCaseId: string | null;
  onSelect: (testCaseId: string) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      {/* Gruppen-Überschrift bleibt einzeilig (truncate statt line-clamp-2) — sie soll nicht
          wachsen, bekommt den ClipTooltip-Anschluss aber genau wie die Prozess-/Testfall-Zeilen. */}
      <ClipTooltip text={label} className="block w-full">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[11px] font-bold uppercase tracking-wide text-text-primary hover:bg-[var(--bg-subtle)]"
        >
          <span>{open ? '▾' : '▸'}</span>
          <span data-clip className="truncate">
            {label}
          </span>
        </button>
      </ClipTooltip>
      {open &&
        processes.map((process) => (
          <TreeProcessNode
            key={process.process_id}
            process={process}
            projectId={projectId}
            selectedTestCaseId={selectedTestCaseId}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

// EXE-3 read-only Baum: schlanke eigene Struktur aus `catalogApi.tree` (statt Wiederverwendung
// von `pages/catalog/process-tree.tsx`, dessen "+ Prozess"/"+ Testfall"-Bearbeitungsaffordanzen
// für die Tester-Ansicht nicht passen).
function MyTestsTree({
  projectId,
  selectedTestCaseId,
  onSelect,
}: {
  projectId: string;
  selectedTestCaseId: string | null;
  onSelect: (testCaseId: string) => void;
}) {
  const { t } = useTranslation();
  const { data: tree, isLoading } = useQuery({
    queryKey: ['catalog-tree', projectId],
    queryFn: () => catalogApi.tree(projectId),
  });

  return (
    // #95: Breite verstellbar (Drag + Pfeiltasten) statt fest w-[360px] — ResizablePanel liefert
    // flex/shrink/border selbst; overflow-y-auto wandert auf den inneren Scroll-Container, weil
    // der Zieh-Griff die volle Panel-Höhe braucht.
    <ResizablePanel
      storageKey="testflow.treeWidth.myTests"
      defaultWidth={380}
      minWidth={280}
      maxWidth={560}
      ariaLabel={t('catalog.tree.resize')}
    >
      <div className="flex-1 overflow-y-auto">
        {isLoading && <div className="p-3 text-sm text-text-muted">{t('catalog.loading')}</div>}
        {tree?.nodes.map((node) => (
          <TreeGroup
            key={node.node_id}
            label={node.name}
            processes={node.processes}
            projectId={projectId}
            selectedTestCaseId={selectedTestCaseId}
            onSelect={onSelect}
          />
        ))}
        {tree && tree.unlabeled.length > 0 && (
          <TreeGroup
            label={t('catalog.tree.unlabeled')}
            processes={tree.unlabeled}
            projectId={projectId}
            selectedTestCaseId={selectedTestCaseId}
            onSelect={onSelect}
          />
        )}
        {tree && tree.nodes.length === 0 && tree.unlabeled.length === 0 && (
          <div className="p-3 text-sm text-text-muted">{t('catalog.empty')}</div>
        )}
      </div>
    </ResizablePanel>
  );
}

function ViewToggle({
  view,
  onChange,
  showChains,
}: {
  view: ViewMode;
  onChange: (view: ViewMode) => void;
  // #128/#127: Ohne Ketten-Reihenfolge („nur Prozesse") gibt es keine Ketten, nach denen sich
  // gruppieren ließe — die Option fehlt dann ganz statt auf eine leere Ansicht zu führen.
  showChains: boolean;
}) {
  const { t } = useTranslation();
  const options: { key: ViewMode; label: string }[] = [
    { key: 'playlist', label: t('exec.view.byStatus') },
    ...(showChains ? [{ key: 'e2e' as const, label: t('exec.view.byChain') }] : []),
    { key: 'tree', label: t('exec.tree') },
  ];
  return (
    <div className="flex overflow-hidden rounded-chip border border-[var(--border)] text-xs">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => onChange(option.key)}
          className={`px-2 py-1 ${
            view === option.key
              ? 'bg-brand-accent text-white'
              : 'text-text-muted hover:text-text-body'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function MyTestsPage() {
  const { t } = useTranslation();
  const { project, isLoading: projectLoading } = useCurrentProject();
  // #36: der Ansichts-Umschalter (Status ⇄ E2E-Prozess ⇄ Baum, #128) lebt in der URL —
  // deep-linkbar, reload-fest. Auf gültige Werte normalisiert, damit ein manipuliertes
  // `?view=xyz` auf die Playlist-Vorgabe zurückfällt. Auswahl (`selected`) und `selectedPlanId` bleiben transienter Ausführungszustand (nicht
  // Teil dieses Issues).
  const [viewParam, setView] = useUrlState('view', 'playlist');
  const requestedView: ViewMode =
    viewParam === 'tree' ? 'tree' : viewParam === 'e2e' ? 'e2e' : 'playlist';
  // #93: eigener Query-Key `my_q` — `q` (Katalog) und `scope_q` (Testplan-Scope) sind an anderer
  // Stelle bereits vergeben (#78/#84).
  const [myQ, setMyQ] = useUrlState('my_q', '');
  // Task 3 (A6): Die Auswahl ist das PAAR (Testfall, Kette) — ein Testfall in zwei Ketten steht
  // zweimal in der Playlist, mit eigenem Zustand. Eine nackte test_case_id könnte die beiden nicht
  // auseinanderhalten (beide Karten markiert, Panel remountet beim Wechsel nicht). `selectedKey`
  // ist die daraus abgeleitete stabile Primitive für React-`key`, Auswahlvergleich und Auto-Sprung
  // — gebildet an der EINEN Stelle (playlistEntryKey), nicht hier nachgebaut.
  // #194: dazu die Karte „Fehler prüfen" als zweite Art der Auswahl (MyTestsSelection oben).
  const [chosen, setChosen] = useState<MyTestsSelection | null>(null);
  const selected = chosen?.kind === 'entry' ? chosen.entry : null;
  const selectedKey = selected ? playlistEntryKey(selected) : null;
  const selectedFixCheckId = chosen?.kind === 'fix_check' ? chosen.defect_id : null;
  const setSelected = useCallback(
    (ref: PlaylistEntryRef | null) => setChosen(ref ? { kind: 'entry', entry: ref } : null),
    [],
  );
  const selectFixCheck = useCallback(
    (defectId: string) => setChosen({ kind: 'fix_check', defect_id: defectId }),
    [],
  );
  const chosenRef = useRef(chosen);
  chosenRef.current = chosen;
  // #194 Final-Review: Zeile mit Liste und Detail — nach dem Sprung (Fehlerprüfung bestätigt) den
  // Fokus auf die gewählte Karte setzen, sonst auf die Suche, und ist die Liste leer (dann gibt es
  // keine Suche), auf die Überschrift der Seite. Die Karte markiert sich selbst
  // (`data-card-selected`, card-frame.tsx): die Fehler-prüfen-Karte ist selbst der Knopf, bei der
  // Testkarte ist es ihr Inhalts-Knopf.
  const listRowRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusSelectedCard = () => {
    const row = listRowRef.current;
    const card = row?.querySelector<HTMLElement>('[data-card-selected]');
    const target = card?.matches('button')
      ? card
      : card?.querySelector<HTMLElement>('button:not([disabled])');
    (
      target ??
      row?.querySelector<HTMLElement>('input[type="search"]') ??
      headingRef.current
    )?.focus();
  };
  // #92: kein weiterer Selektor-Umbau, nur der can()-Blick für den Defect-Chip der Retest-Sektion
  // (Muster app-shell.tsx:256-260) — die Playlist kennt selbst keine Rollen.
  const { user } = useScopedUser();
  const canViewDefect = user ? canInAnyScope(user, 'defect.edit') : false;
  // #195: Abgeben/Übernehmen braucht `run.handover` (admin, testmanager, verantwortlicher, tester).
  // Ohne das Recht gibt es weder die Übersicht-Abfrage noch Kästchen — der Server prüft dasselbe.
  const canHandover = user ? canInAnyScope(user, 'run.handover') : false;
  // #194: Karten „Fehler prüfen" nur mit `defect.confirm_fix` (admin, testmanager, verantwortlicher,
  // tester) — ohne das Recht keine Abfrage. Wer tatsächlich Prüfer ist, entscheidet der Server.
  const canConfirmFix = user ? canInAnyScope(user, 'defect.confirm_fix') : false;
  const queryClient = useQueryClient();
  // #192: Defect, der gerade in der Nur-Lesen-Seitenleiste offen ist — aus der Liste unter der
  // Ausführung oder über den Retest-Chip der Playlist.
  const [openDefectId, setOpenDefectId] = useState<string | null>(null);
  // #195: Seitenleiste „Tests von Kollegen" (Knopf im Kopf, nur bei übernehmbaren Einträgen).
  const [colleaguesOpen, setColleaguesOpen] = useState(false);
  const colleaguesButtonRef = useRef<HTMLButtonElement>(null);
  // Stabil halten: SidePanel holt in einem Effekt auf [onClose] den Fokus ins Panel. Mit einer pro
  // Render neuen Funktion zöge jeder Rerender (Refetch, Mutation pending) den Fokus zurück auf den
  // Dialog. Beim Schließen geht der Fokus an den Knopf zurück, der die Leiste geöffnet hat.
  const closeColleagues = useCallback(() => {
    setColleaguesOpen(false);
    colleaguesButtonRef.current?.focus();
  }, []);
  const closeDefect = useCallback(() => setOpenDefectId(null), []);

  const projectId = project?.project_id ?? '';

  const plansQuery = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const { data: plans, isLoading: plansLoading, isError: plansError } = plansQuery;

  const activePlans = sortActivePlans(plans ?? []);
  const activePlanIdsKey = activePlans.map((p) => p.test_plan_id).join(',');
  const storageKey = projectId ? `testflow.activePlan.${projectId}` : null;

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // #55: Vorbelegung ist die vom Nutzer PRO PROJEKT gemerkte Wahl aus `localStorage`, sofern der
  // gemerkte Plan noch aktiv ist — sonst greift `defaultActivePlanId` (nächster Zieltermin
  // zuerst, s. plan-selection.ts). Der Effekt hängt bewusst an der ID-LISTE der aktiven Pläne,
  // nicht am Array-OBJEKT `activePlans` — sonst würde jeder Refetch (z. B. Fensterfokus) die Wahl
  // stillschweigend überschreiben (dieselbe Falle wie in reporting-page.tsx/signoff-page.tsx). Eine
  // bereits gültige Auswahl bleibt deshalb zuerst erhalten (funktionales Update liest `cur` ohne
  // ihn in die Deps aufnehmen zu müssen); nur wenn sie nicht mehr existiert, wird neu bestimmt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusst nur ID-Liste + storageKey, s. o.
  useEffect(() => {
    setSelectedPlanId((cur) => {
      if (cur && activePlans.some((p) => p.test_plan_id === cur)) return cur;
      const stored = storageKey ? localStorage.getItem(storageKey) : null;
      const next =
        stored && activePlans.some((p) => p.test_plan_id === stored)
          ? stored
          : defaultActivePlanId(plans ?? []);
      // Wechselt der Plan hier AUTOMATISCH — weil der bisher gewählte inzwischen deaktiviert
      // wurde und der Refetch das mitbringt —, muss der gewählte Testfall genauso fallen wie beim
      // Wechsel von Hand. Sonst schickt der Tester seinen naechsten Lauf mit einem Testfall aus
      // dem ALTEN Plan an den NEUEN: falscher Plan, falscher Scope, und er merkt es nicht.
      if (cur && next !== cur) setChosen(null);
      return next;
    });
  }, [activePlanIdsKey, storageKey]);

  function selectPlan(id: string) {
    setSelectedPlanId(id);
    if (storageKey) localStorage.setItem(storageKey, id);
    // Der bisher gewählte Testfall gehört zum Scope des ALTEN Plans — ohne Reset würde das
    // Ausführungspanel (das über `key={selectedKey}` remountet) einen Testfall zeigen, der
    // im neuen Plan gar nicht im Scope ist. #194: Dasselbe gilt für eine gewählte Fehlerprüfung.
    setChosen(null);
  }

  const playlistQuery = useQuery({
    queryKey: ['playlist', projectId, selectedPlanId],
    queryFn: () => runsApi.playlist(projectId, selectedPlanId ?? ''),
    enabled: Boolean(projectId) && Boolean(selectedPlanId),
  });
  const { data: playlist, isLoading: playlistLoading, isError: playlistError } = playlistQuery;

  // #92: Deeplink aus der retest_due-Benachrichtigung (`/my-tests?case=<id>`, Muster wie der
  // Katalog-`?test_case=`, s. notification-deeplink.ts). Bewusst NICHT dauerhaft gespiegelt wie
  // beim Katalog: der Auto-Sprung (EXE-1, s. u.) ändert die Auswahl laufend, das soll
  // nicht bei jedem Sprung in die URL geschrieben werden. Der Parameter wird deshalb nur EINMAL
  // zum Vorselektieren gelesen, sobald die Playlist geladen ist, und danach konsumiert (entfernt)
  // — unabhängig davon, ob der Testfall darin gefunden wurde (sonst bliebe er nach einem
  // Plan-Wechsel stehen und würde dort eine andere Auswahl vorbelegen).
  const [caseParam, setCaseParam] = useUrlState('case', '');
  useEffect(() => {
    if (!caseParam || !playlist) return;
    // Task 3: Der Deeplink bleibt TESTFALL-bezogen — eine Benachrichtigung kennt die Kette nicht.
    // Steht der Testfall in mehreren Ketten, wird die Kette des gewählten Eintrags mit übernommen,
    // damit die Auswahl ein vollständiges Paar ist.
    //
    // Review-Befund Important 2 (Task 10): und zwar BEVORZUGT ein nicht gesperrter Eintrag. Maus
    // und Tastatur sind an der gesperrten Karte dicht, der Deeplink war der dritte Weg ins
    // Ausführungsfeld und sah den Zustand gar nicht an — der Tester landete auf einem Glied, dessen
    // Vorgänger offen ist, füllte aus und bekam 409 `predecessor_not_done`. „Bevorzugt", nicht
    // „nur": steht der Testfall ausschließlich gesperrt in der Playlist, ist die leere Auswahl
    // keine bessere Antwort auf die Benachrichtigung — dort fängt die Sperre im Ausführungsfeld
    // selbst (execution-panel.tsx, `predecessorBlocked`).
    const matches = playlist.filter((entry) => entry.test_case_id === caseParam);
    const hit = matches.find((entry) => !isBlocked(entry)) ?? matches[0];
    if (hit) {
      setSelected({ test_case_id: hit.test_case_id, e2e_id: hit.e2e_id });
    }
    setCaseParam('');
  }, [caseParam, playlist, setCaseParam, setSelected]);

  // #194: Fehlerprüfungen (Abfrage, Deeplink `?fixcheck=`, Bestätigung) — use-fix-checks.ts. Die
  // Auswahl bleibt hier: der Deeplink wählt die Karte aus, nach dem Bestätigen springt die Auswahl
  // auf die nächste Fehlerprüfung, sonst auf die nächste offene Testkarte, sonst ins Leere.
  const fix = useFixChecks({
    projectId,
    canConfirmFix,
    userLoaded: Boolean(user),
    selectedPlanId,
    selectPlan,
    onDeeplink: selectFixCheck,
    onConfirmed: (check, before, fresh) => {
      const trimmed = myQ.trim();
      const nextCheck = nextFixCheck(
        before,
        fresh.filter((c) => c.test_plan_id === selectedPlanId && fixCheckMatches(trimmed, c)),
        check.defect_id,
      );
      // Fix-Runde 1: erst entscheiden, OB gesprungen wird — nur dann Ziel und Filter-Hinweis. Hat der
      // Prüfer während des Neuladens selbst etwas anderes gewählt, bleibt das, und es gibt keinen
      // Hinweis zu einem Sprung, der nicht stattfindet. Die Ref trägt die Auswahl des letzten Renders.
      const isConfirmed = (cur: MyTestsSelection | null) =>
        cur === null || (cur.kind === 'fix_check' && cur.defect_id === check.defect_id);
      if (!isConfirmed(chosenRef.current)) return;
      let next: MyTestsSelection | null = null;
      if (nextCheck) {
        next = { kind: 'fix_check', defect_id: nextCheck.defect_id };
      } else {
        // Keine Fehlerprüfung mehr: die nächste offene Testkarte nach der EXE-1-Regel, ab
        // Listenanfang (die Karten „Fehler prüfen" stehen in keiner Position der Playlist).
        const { next: entryNext, skippedFiltered } = nextAfterSubmit(playlist ?? [], null, (e) =>
          matchesQuery(trimmed, e.human_id, e.title_de, e.title_en),
        );
        if (skippedFiltered) toast.info(t('exec.skippedFiltered'));
        if (entryNext) {
          next = {
            kind: 'entry',
            entry: { test_case_id: entryNext.test_case_id, e2e_id: entryNext.e2e_id },
          };
        }
      }
      // Dieselbe Bedingung noch einmal im Update — falls zwischen Render und Sprung gewählt wurde.
      // #194 Final-Review: `flushSync` — der Sprung steht im DOM, bevor die Mutation „fertig" meldet.
      // Sonst konnte ein Render mit frischer Liste und alter Auswahl dazwischenkommen: rechts kurz
      // „Testfall wählen". Danach liegt die Karte im DOM, und der Fokus kann auf sie (der geklickte
      // Knopf ist mit dem alten Detail verschwunden); ohne neue Auswahl auf die Suche der Liste.
      let jumped = false;
      flushSync(() =>
        setChosen((cur) => {
          if (!isConfirmed(cur)) return cur;
          jumped = true;
          return next;
        }),
      );
      if (jumped) focusSelectedCard();
    },
  });
  const planFixChecks = fix.planFixChecks;
  // #194 Final-Review: Solange die Bestätigung läuft, bleibt ihr Detail stehen, auch wenn die neu
  // geladene Liste die Karte schon nicht mehr enthält (die Mutation wartet noch auf weitere
  // Abfragen). Sonst stand dazwischen „Testfall wählen" da, und die Regel unten höbe die Auswahl auf.
  const pendingCheck = fix.pendingCheck?.defect_id === selectedFixCheckId ? fix.pendingCheck : null;
  const selectedFixCheck =
    planFixChecks.find((c) => c.defect_id === selectedFixCheckId) ?? pendingCheck;
  // Fällt die gewählte Prüfung beim Neuladen weg (veralteter Stand 409/404, anderswo bearbeitet), fällt
  // die Auswahl mit — kein Detail zu einer Karte, die es nicht mehr gibt. Funktionales Update: nur
  // wenn noch DIESE Prüfung gewählt ist (der Sprung nach dem Bestätigen kann schneller sein).
  const fixChecksLoaded = fix.allFixChecks !== undefined;
  const selectedFixCheckGone = selectedFixCheckId !== null && fixChecksLoaded && !selectedFixCheck;
  useEffect(() => {
    if (!selectedFixCheckGone) return;
    setChosen((cur) =>
      cur?.kind === 'fix_check' && cur.defect_id === selectedFixCheckId ? null : cur,
    );
  }, [selectedFixCheckGone, selectedFixCheckId]);
  const fixCheckProps = {
    fixChecks: planFixChecks,
    selectedFixCheckId,
    onSelectFixCheck: selectFixCheck,
    // Solange die Prüfungen noch laden, ist eine leere Playlist kein „nichts zu tun".
    fixChecksLoading: fix.query.isLoading,
    onRetryFixChecks: fix.query.isError ? () => void fix.query.refetch() : undefined,
  };
  // #194: Testfall aus dem Detail rechts, in der Seitenleiste. Stabil (SidePanel holt in einem Effekt
  // auf [onClose] den Fokus). Escape schließt nur sie — die Seite hat keinen eigenen Escape-Griff,
  // der zugleich die Auswahl leeren würde.
  const [openTestCaseId, setOpenTestCaseId] = useState<string | null>(null);
  // #194 Final-Review: beim Schließen geht der Fokus an den Testfall-Knopf im Detail zurück (Muster
  // closeColleagues). Der Knopf liegt in MyDefectDetails — er reicht sich beim Öffnen selbst mit.
  const testCaseTriggerRef = useRef<HTMLElement | null>(null);
  const openTestCase = useCallback((testCaseId: string, trigger: HTMLElement) => {
    testCaseTriggerRef.current = trigger;
    setOpenTestCaseId(testCaseId);
  }, []);
  const closeTestCase = useCallback(() => {
    setOpenTestCaseId(null);
    testCaseTriggerRef.current?.focus();
  }, []);

  // GAM-1/GAM-2: derselbe gewählte Plan wie die Playlist oben (kein zweiter Plan-Selektor). Der
  // Server fasst Projekt-Toggle + User-Opt-out bereits in `stats.enabled` zusammen — die drei
  // Gamification-Komponenten rendern dann selbst `null`.
  const { data: gamStats } = useQuery({
    queryKey: ['gam-stats', projectId, selectedPlanId],
    queryFn: () => gamificationApi.stats(projectId, selectedPlanId ?? ''),
    enabled: Boolean(projectId) && Boolean(selectedPlanId),
  });

  // EXE-1 nach #93: Kandidatenwahl kommt aus lib/playlist-groups (eine Regelquelle mit der
  // Anzeige). Weggefilterte werden übersprungen + EINMAL je Sprung gemeldet — der Tester hat den
  // Filter bewusst gesetzt (Klärungsprotokoll), soll ihn aber nicht vergessen. Der Server-Stand
  // der Playlist wird vom ExecutionPanel selbst invalidiert (Query-Key ['playlist', ...]).
  const advanceSelection = () => {
    const trimmed = myQ.trim();
    const { next, skippedFiltered } = nextAfterSubmit(playlist ?? [], selectedKey, (e) =>
      matchesQuery(trimmed, e.human_id, e.title_de, e.title_en),
    );
    if (skippedFiltered) toast.info(t('exec.skippedFiltered'));
    setSelected(next ? { test_case_id: next.test_case_id, e2e_id: next.e2e_id } : null);
  };

  // #127: Ob im gewählten Plan eine Ketten-Reihenfolge gilt, entscheidet allein sein scope_mode
  // (dieselbe Funktion wie auf dem Server). Ohne gefundenen Plan bleibt es beim Bisherigen (true).
  const selectedPlan = activePlans.find((p) => p.test_plan_id === selectedPlanId);
  const enforced = chainOrderEnforced(selectedPlan?.scope_mode);
  // #128: `?view=e2e` in einem „nur Prozesse"-Plan zeigt die Status-Ansicht. Der URL-Wert bleibt
  // stehen — wechselt der Tester in einen Plan mit Ketten, ist seine Wahl wieder da.
  const view: ViewMode = requestedView === 'e2e' && !enforced ? 'playlist' : requestedView;
  // #194 Fix-Runde 1: Der Baum zeigt keine Karten „Fehler prüfen" — eine dort gewählte Prüfung ließe
  // rechts ein Detail ohne Karte stehen. Beim Wechsel in den Baum (auch per Deeplink mit view=tree, wo
  // `?fixcheck=` erst nach dem Laden auswählt) fällt eine solche Auswahl daher weg.
  useEffect(() => {
    if (view !== 'tree' || !selectedFixCheckId) return;
    setChosen((cur) => (cur?.kind === 'fix_check' ? null : cur));
  }, [view, selectedFixCheckId]);

  // #195: Weitergabe in der Ansicht „Status". Übersicht nur mit Recht und Plan. Die Markierung
  // (Schlüssel = test_case_id, W3: weitergegeben wird der Testfall) leert sich bei jedem Wechsel
  // von Plan, Suche oder Ansicht — Regel aus #91: eine Sammelaktion darf nie unsichtbar markierte
  // Zeilen treffen. Die Ansicht gehört mit in den Schlüssel, weil E2E- und Baum-Ansicht keine
  // Kästchen zeigen und die Markierung dort sonst unbemerkt stehen bliebe.
  const handoverQuery = useQuery({
    queryKey: ['handover', projectId, selectedPlanId],
    queryFn: () => handoverApi.overview(projectId, selectedPlanId ?? ''),
    enabled: canHandover && Boolean(projectId) && Boolean(selectedPlanId),
  });
  const selection = useRowSelection(`${selectedPlanId}|${myQ}|${view}`);

  const handoverMutation = useMutation({
    // `subject`: bei give der Name des Kollegen, bei take die ID des übernommenen Tests — für den
    // Toast. Leer, wenn nicht auflösbar (dann neutraler Text ohne Namen).
    mutationFn: (v: { planId: string; body: HandoverRequest; subject: string }) =>
      handoverApi.apply(projectId, v.planId, v.body),
    onSuccess: (result, v) => {
      selection.clear();
      for (const queryKey of [
        ['playlist', projectId, v.planId],
        ['handover', projectId, v.planId],
        ['notifications'],
        // Kettenleiste „liegt bei" und die Ketten-Ansicht lesen die Zuweisung live.
        ['e2e-chain', projectId, v.planId],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }

      // Der Toast zählt Einträge (moved_entries), nicht Testfälle (CONTEXT.md). Teilerfolg statt
      // Alles-oder-nichts (W6): die Übersprungenen werden ebenfalls in Einträgen gezählt; hat ein
      // Übersprungener keine Karte hier (beim Übernehmen immer), zählt er als einer.
      const skippedEntries = Math.max(
        selectedEntryCount(new Set(result.skipped.map((x) => x.test_case_id)), playlist ?? []),
        result.skipped.length,
      );
      const total = result.moved_entries + skippedEntries;
      if (v.body.mode === 'give') {
        // Ohne auflösbaren Namen den neutralen Text nehmen, nie „an  weitergegeben".
        const neutral = v.subject === '' ? 'Neutral' : '';
        if (result.skipped.length === 0) {
          toast.success(
            t(`handover.toast.given${neutral}`, { count: result.moved_entries, name: v.subject }),
          );
        } else {
          toast.info(
            t(`handover.toast.partial${neutral}`, {
              moved: result.moved_entries,
              total,
              count: skippedEntries,
              name: v.subject,
            }),
          );
        }
      } else if (result.skipped.length === 0) {
        toast.success(t('handover.toast.taken', { id: v.subject }));
      } else {
        toast.info(
          t('handover.toast.takenPartial', {
            moved: result.moved_entries,
            total,
            count: skippedEntries,
          }),
        );
      }

      // Steht die Ausführung auf einem gerade abgegebenen Test, würde das Panel bis zum Neuladen
      // einen fremden Test zeigen. Sprung wie nach einem Lauf (EXE-1) auf den nächsten verbleibenden
      // Eintrag — die abgegebenen Karten zählen dabei nicht mehr als Ziel. Der gewählte Eintrag
      // bleibt als Ankerpunkt in der Liste, damit der Sprung an seiner Stelle weiterläuft.
      const movedIds = new Set(result.moved);
      if (v.planId === selectedPlanId && selected && movedIds.has(selected.test_case_id)) {
        const remaining = (playlist ?? []).filter(
          (e) => !movedIds.has(e.test_case_id) || playlistEntryKey(e) === selectedKey,
        );
        const trimmed = myQ.trim();
        const { next } = nextAfterSubmit(remaining, selectedKey, (e) =>
          matchesQuery(trimmed, e.human_id, e.title_de, e.title_en),
        );
        setSelected(next ? { test_case_id: next.test_case_id, e2e_id: next.e2e_id } : null);
      }
    },
    onError: (err) => toast.error(mapApiError(t, err)),
  });

  const handoverOverview = handoverQuery.data;
  const handover: PlaylistHandover | undefined =
    canHandover && handoverOverview && selectedPlanId
      ? {
          overview: handoverOverview,
          // Nur Testfälle, die der Server weiterhin als weitergebbar meldet: nach einem Neuladen
          // kann ein markierter Test inzwischen gesperrt oder weg sein.
          selected: new Set(
            handoverOverview.mine
              .filter((m) => selection.selected.has(m.test_case_id))
              .map((m) => m.test_case_id),
          ),
          onToggle: selection.toggle,
          onClear: selection.clear,
          onGive: (toUserId) =>
            handoverMutation.mutate({
              planId: selectedPlanId,
              body: {
                mode: 'give',
                test_case_ids: handoverOverview.mine
                  .filter((m) => selection.selected.has(m.test_case_id))
                  .map((m) => m.test_case_id),
                to_user_id: toUserId,
              },
              subject: handoverOverview.colleagues.find((c) => c.user_id === toUserId)?.name ?? '',
            }),
          pending: handoverMutation.isPending,
        }
      : undefined;

  // „Tests von Kollegen" (ColleaguesPanel): ein Klick übernimmt einen Testfall. Gleiche Mutation
  // wie „Weitergeben", damit Toast, Teilerfolg und Invalidierung an einer Stelle bleiben.
  const takeFromColleague = (testCaseId: string) => {
    if (!handoverOverview || !selectedPlanId) return;
    handoverMutation.mutate({
      planId: selectedPlanId,
      body: { mode: 'take', test_case_ids: [testCaseId] },
      subject:
        handoverOverview.colleagues
          .flatMap((c) => c.entries)
          .find((e) => e.test_case_id === testCaseId)?.human_id ?? '',
    });
  };
  const colleagueEntryCount = handover ? countColleagueEntries(handover.overview.colleagues) : 0;

  const gamFooter = gamStats?.enabled ? (
    <div className="flex flex-col gap-3">
      <TeamGoalBar stats={gamStats} />
      <CoverageMilestone stats={gamStats} />
    </div>
  ) : undefined;

  if (projectLoading || plansLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }

  // #89: VOR dem Leerzustand geprüft — sonst feiert `activePlans.length === 0` einen gescheiterten
  // Fetch als "Keine offenen Tests — stark! 🎉" statt ihn zu melden (Kernsatz: ein Query-Fehler
  // darf NIE als Leerzustand erscheinen).
  if (plansError) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-lg font-bold text-text-primary">{t('exec.myTests')}</h1>
        <QueryErrorNotice onRetry={() => plansQuery.refetch()} />
      </div>
    );
  }

  if (!project || activePlans.length === 0) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-lg font-bold text-text-primary">{t('exec.myTests')}</h1>
        <div className="text-sm text-text-muted">{t('exec.empty')}</div>
      </div>
    );
  }

  return (
    // #211 Nachtrag: Die Seite ist auf die Fensterhöhe unter der Kopfleiste (`h-[58px]` in
    // app-shell.tsx) begrenzt. Vorher wuchs sie mit der Liste, und bei vielen Tests scrollte das
    // ganze Fenster — Kopfleiste, Ansichtsumschalter und Ausführung liefen mit aus dem Bild. Jetzt
    // scrollen Liste und Ausführung jede für sich (beide Spalten tragen schon `overflow-y-auto`).
    <div className="flex h-[calc(100dvh-58px)] flex-col">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
        <div className="flex items-center gap-3">
          {/* tabIndex -1: Fokusziel nach dem Bestätigen einer Fehlerprüfung, wenn nichts mehr da ist. */}
          <h1 ref={headingRef} tabIndex={-1} className="text-lg font-bold text-text-primary">
            {t('exec.myTests')}
          </h1>
          {gamStats && <StreakChip stats={gamStats} />}
        </div>
        <div className="flex items-center gap-3">
          {/* #195: nur mit übernehmbaren Einträgen da (nicht bloß deaktiviert) — ohne Kollegen
              sieht „Meine Tests" aus wie vorher. In jeder Ansicht, weil der Kopf nicht von ihr
              abhängt. */}
          {handover && colleagueEntryCount > 0 && (
            <button
              ref={colleaguesButtonRef}
              type="button"
              onClick={() => setColleaguesOpen(true)}
              className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white"
            >
              {t('handover.colleagues.button', { count: colleagueEntryCount })}
            </button>
          )}
          {/* #55: Umschalter nur ab zwei gleichzeitig aktiven Plänen — bei genau einem bleibt die
              Ansicht so schlank wie vorher (kein Selektor für eine Auswahl ohne Alternative). */}
          {activePlans.length > 1 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-text-muted">{t('myTests.plan')}</span>
              <select
                className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1"
                value={selectedPlanId ?? ''}
                onChange={(e) => selectPlan(e.target.value)}
              >
                {activePlans.map((p) => (
                  <option key={p.test_plan_id} value={p.test_plan_id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <ViewToggle view={view} onChange={setView} showChains={enforced} />
        </div>
      </div>
      {/* overflow-clip statt -hidden: hidden machte die Zeile zum Scroll-Container, und die
          Weitergabe-Leiste (#195, `sticky bottom-0`) klebte dann an dieser nie scrollenden Zeile
          statt am Viewport. #211: `min-h-0`, damit die Zeile in der begrenzten Seitenhöhe
          schrumpfen darf und die Spalten selbst scrollen. Die Weitergabe-Leiste steht seitdem
          ohnehin am Fuß der Listenspalte, unter deren Scrollbereich. */}
      <div ref={listRowRef} className="flex min-h-0 flex-1 overflow-clip">
        {view === 'tree' ? (
          <MyTestsTree
            projectId={projectId}
            selectedTestCaseId={selected?.test_case_id ?? null}
            // Task 3 (bewusste Entscheidung): Der Baum kennt KEINE Kette — er ist die
            // katalogorientierte Sicht, die Kettenwahl gehört in die Playlist. Eine Auswahl von
            // hier ist deshalb immer der kettenlose Eintrag (e2e_id = null).
            onSelect={(testCaseId) => setSelected({ test_case_id: testCaseId, e2e_id: null })}
          />
        ) : playlistError ? (
          // #89: sonst rendert Playlist selbst `entries.length === 0` -> denselben `exec.empty`
          // -Text, der einen gescheiterten Fetch als leere Liste tarnt. Gilt für beide Listen-
          // Ansichten (Status und E2E-Prozess) — sie lesen dieselbe Playlist.
          <div className="w-[360px] shrink-0 border-r border-[var(--border)] p-3">
            <QueryErrorNotice onRetry={() => playlistQuery.refetch()} />
          </div>
        ) : view === 'e2e' ? (
          // #128: dieselbe Playlist, nach Kette gruppiert. Auswahl und Auto-Sprung bleiben
          // unverändert (nextAfterSubmit über die Server-Reihenfolge der Playlist).
          <ChainGroups
            projectId={projectId}
            planId={selectedPlanId ?? ''}
            entries={playlist ?? []}
            isLoading={playlistLoading}
            selectedKey={selectedKey}
            onSelect={setSelected}
            canViewDefect={canViewDefect}
            onOpenDefect={setOpenDefectId}
            search={myQ}
            onSearchChange={setMyQ}
            footer={gamFooter}
            {...fixCheckProps}
          />
        ) : (
          <Playlist
            entries={playlist ?? []}
            isLoading={playlistLoading}
            selectedKey={selectedKey}
            onSelect={setSelected}
            canViewDefect={canViewDefect}
            onOpenDefect={setOpenDefectId}
            planId={selectedPlanId ?? ''}
            search={myQ}
            onSearchChange={setMyQ}
            footer={gamFooter}
            handover={handover}
            {...fixCheckProps}
          />
        )}
        {/* #194 (Nachtrag 14.10.): Ist eine Karte „Fehler prüfen" gewählt, steht hier ihr Detail statt
            der Ausführung — `key` je Defect, damit ein halber „Nicht behoben"-Grund nie an einen
            anderen Defect wandert. */}
        <div className="flex-1 space-y-6 overflow-y-auto">
          {selectedFixCheck ? (
            <FixCheckDetail
              key={selectedFixCheck.defect_id}
              projectId={projectId}
              check={selectedFixCheck}
              pending={fix.pendingId === selectedFixCheck.defect_id}
              onConfirm={(body) =>
                fix.confirm(
                  selectedFixCheck,
                  body,
                  planFixChecks.filter((c) => fixCheckMatches(myQ.trim(), c)),
                )
              }
              onOpenTestCase={openTestCase}
              canViewDefect={canViewDefect}
            />
          ) : (
            <>
              <ExecutionPanel
                // Task 3: Remount am PAAR — beim Wechsel zwischen zwei Ketten DESSELBEN Testfalls
                // bliebe sonst der halbfertige Panel-Zustand (Fehler-Dialog, Screenshots,
                // Testdaten-Auswahl) stehen und liefe in die falsche Kette.
                key={selectedKey ?? 'none'}
                projectId={projectId}
                planId={selectedPlanId ?? ''}
                testCaseId={selected?.test_case_id ?? null}
                e2eId={selected?.e2e_id ?? null}
                chainOrderEnforced={enforced}
                onSubmitted={advanceSelection}
              />
              {/* #31: der Tester verlor seine Meldung bisher aus dem Blick, sobald er abgeschickt
              hatte — die Defect-Routen verlangen defect.edit, das er nicht hat.
              #192: nur die Meldungen zum gewählten Testfall; Details in der Seitenleiste. */}
              <MyDefects
                projectId={projectId}
                testCaseId={selected?.test_case_id ?? null}
                onOpen={setOpenDefectId}
              />
            </>
          )}
        </div>
      </div>
      {colleaguesOpen && handover && (
        <ColleaguesPanel
          projectId={projectId}
          colleagues={handover.overview.colleagues}
          onTake={takeFromColleague}
          onClose={closeColleagues}
          pending={handover.pending}
        />
      )}
      {openDefectId && (
        <MyDefectPanel projectId={projectId} defectId={openDefectId} onClose={closeDefect} />
      )}
      {openTestCaseId && (
        <TestCasePanel projectId={projectId} testCaseId={openTestCaseId} onClose={closeTestCase} />
      )}
    </div>
  );
}
